/* CQ related products (sections/cq-related-products.liquid) and replacement part cards (snippets/cq-related-parts.liquid),
   round 3 B5, round 4 (2026-10-15).
   1. cq-related-parts: shows the selected option's replacement parts when the option picker sends cq:variant-change
      (assets/cq-option-picker.js; also the older variant:change on document). Only events naming one of this
      product's variants count, so a quick view of another product changes nothing. The same element works at the top
      of the product page's Parts tab and in the related band; in the tab the tab script moves it between the desktop
      panel and the phone accordion, so it takes its listeners off and puts them back on each move.
   2. Complementary fallback: when the product has no list, asks Shopify's complementary recommendations through
      this section, 1500px ahead or when the browser is idle (as PERF-01), and shows the group only when cards come
      back. Products named as parts are left out. Round 4 fix pass item C (approved 2026-10-15 06:48): companions
      only, so Shopify's "related" suggestions are no longer asked for; fewer cards, or no row, instead.
   3. RELATED-3: a wrap (.cq-rel-wrap, padding included) is hidden while no group shows and no fallback request is
      pending, so an empty section takes no space.
   4. R4 finish: a "Goes well with" card is kept only when it is a real card with a photo. card-product.liquid prints
      only a template for the options app's helper products (OPTIONS_HIDDEN_PRODUCT, mws_apo_generated) and gift
      cards, which showed as a blank card; a product with no photo prints the placeholder drawing. The Liquid leaves
      them out too (with sold out and not on the Online Store); this is the same check on what arrives.
   5. Round 4 placement: a "Goes well with" row printed for "Under the photos and buy box" (data-place="hero") moves
      into the main product section's slot between the hero and the tabs ([data-cq-pdp-after-hero]) before it starts.
      Liquid cannot put it there: the tabs are printed inside the main product section. Without the slot (another
      main section) or without this script it stays where the section sits. Theme editor: a re-rendered section brings
      a new copy, which replaces the moved one; a re-rendered main section gets the moved row back. Round 4.3
      (2026-10-15): "After the product tabs" (data-place="tabs") moves the row the same way into the slot right after
      the tabs ([data-cq-pdp-after-tabs]); without that slot it stays where the section sits.
   6. Headings: a group with nothing to show at first paint keeps its heading in <template data-cq-rel-head>; it is
      printed when the group gets cards, and put back in its template when the group loses its last card, so the page
      never carries an empty heading.
   7. Round 4 fix pass item B (approved 2026-10-15 06:48): "Goes well with" leaves out every product the page already
      sells as an add-on. The SC Product Options app (Bold) draws this product's add-ons in the browser, after load,
      in .bold_options[data-product-id]; every value with a product behind it names that product's handle
      (data-product-handle). Until the app has drawn them, or has answered with none, the row keeps its place but
      stays invisible, so no add-on card is ever seen; it waits at most ADDON_WAIT ms, then shows, and an add-on drawn
      later still takes its card out. While it waits it keeps its height, so nothing under it moves when it shows. A card's handle is its data-cq-handle, else its product link.
   8. Round 4 fix pass item A (approved 2026-10-15 06:48): in the Parts tab, an old parts table typed into the product
      (Replacement Cartridges and the like) whose every row already has a part card, compared by SKU, is left out with
      its own heading; a table that lists anything more (another part, an optional cartridge, media) stays. Every
      option's cards count, so the result does not change with the option. Round 4.2 (2026-10-15): the Description
      block's "Hide the old parts table when cards cover it" (on by default) switched off prints data-cq-rel-keep-tables
      on the cards' element, and then every table stays under the parts. The cards' two styles (Cards and Compact list)
      are CSS only; nothing here depends on them.
   9. 2026-10-15 (whole house RO reference): a short line that points to a table left out ("Check the system spec
      chart for the filters and sizes compatible with your system.") is left out with it: a paragraph of at most 200
      characters that names a chart or a table, right next to the table or right above its left-out heading (only
      blank lines between). No line on the page then points to a chart that is not shown. A line under a left-out
      table whose next thing (past blank lines) is a table that stays shown introduces that table and stays.
   10. The size comparison under the parts (snippets/cq-parts-compare.liquid, "Compare replacement parts for every
      size") marks the selected option's row; the mark follows the option picker with the cards.
   11. Customers also bought (2026-10-18, AJ: "customers also bought is good to add"): the second group of the row
      ([data-cq-rel-cab]) gets the same treatment as "Goes well with": it waits for the add-on app with it (item 7),
      and its cards that are not real cards or that the page sells as add-ons are taken out. It hides, heading
      stashed, when fewer cards than its minimum (data-cq-rel-min) are left. It never asks Shopify for more. */
(() => {
  const moved = new Map(); // section id -> the wrap moved under the buy box
  const ADDON_WAIT = 8000; // item 7: the longest the row waits for the add-on app (as the option picker's own release)
  const ADDON_DRAW = 1200; // item 7: after the app's answer, how long it may take to draw before "no add-ons" holds
  // Item 5: where each place's row moves (round 4.3 adds the slot after the tabs).
  const SLOTS = { hero: '[data-cq-pdp-after-hero]', tabs: '[data-cq-pdp-after-tabs]' };

  // Prints a group's waiting heading (item 6).
  const printHead = (group) => {
    const tpl = group && group.querySelector(':scope > template[data-cq-rel-head]');
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
  };
  // Puts a printed heading back in its template (item 6), when its group has lost every card.
  const stashHead = (group) => {
    const head = group && group.querySelector(':scope > .cq-rel-head');
    if (!head) return;
    const tpl = document.createElement('template');
    tpl.setAttribute('data-cq-rel-head', '');
    head.replaceWith(tpl);
    tpl.content.append(head);
  };

  // Item 8: an old parts table in the Parts tab whose every row names a part card's SKU.
  const SKU_SPLIT = /[^A-Z0-9-]+/;
  const textOf = (node) => (node?.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  const MEDIA = 'img, picture, video, iframe, svg, table, input, select, button';
  const SKIP = new Set(['STYLE', 'SCRIPT', 'TEMPLATE', 'LINK', 'META', 'BR', 'NOSCRIPT']);
  // Nothing a shopper sees: blank text, an empty paragraph or line break, a style block, something already left out.
  const isBlank = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return !textOf(node);
    if (node.nodeType !== Node.ELEMENT_NODE) return true;
    if (SKIP.has(node.tagName) || node.hidden || node.hasAttribute('data-cq-rel-covered')) return true;
    return !textOf(node) && !node.matches(MEDIA) && !node.querySelector(MEDIA);
  };
  const isHeading = (node) => node.nodeType === Node.ELEMENT_NODE && (/^H[1-6]$/.test(node.tagName) || node.getAttribute('role') === 'heading');
  // Item 9: a short paragraph that points to a chart or table.
  const LEAD = /\b(chart|table)s?\b/i;
  const isLead = (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'P' && !node.hidden && !node.hasAttribute('data-cq-rel-covered')
    && textOf(node).length <= 200 && LEAD.test(textOf(node)) && !node.querySelector(MEDIA);
  const tableCovered = (table, skus) => {
    let rows = 0;
    for (const [i, row] of [...table.rows].entries()) {
      const text = textOf(row).toUpperCase();
      if (!text) continue;
      // A header row: only th cells, or a first row with no digit ("Product Name | SKU").
      if (![...row.cells].some((cell) => cell.tagName === 'TD') || (i === 0 && !/\d/.test(text))) continue;
      rows += 1;
      if (!text.split(SKU_SPLIT).some((token) => skus.has(token.replace(/^-+|-+$/g, '')))) return false;
    }
    return rows > 0;
  };

  if (!customElements.get('cq-related-parts')) {
    customElements.define('cq-related-parts', class extends HTMLElement {
      connectedCallback() {
        if (!this.map) {
          const mapEl = this.querySelector('[data-cq-rel-map]');
          try { this.map = JSON.parse(mapEl ? mapEl.textContent : '{}').v || {}; } catch (e) { this.map = {}; }
          this.current = this.dataset.selected || null;
          if (this.classList.contains('cq-rel-in-tab')) this.coverTables();
        }
        this.listeners?.abort();
        this.listeners = new AbortController();
        const onVariant = (event) => {
          const id = event.detail && event.detail.variant && String(event.detail.variant.id);
          if (id && id !== this.current && this.map[id]) this.showParts(id);
        };
        document.addEventListener('cq:variant-change', onVariant, { signal: this.listeners.signal });
        document.addEventListener('variant:change', onVariant, { signal: this.listeners.signal });
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.listeners = null;
      }

      // Item 8: leaves out, once, each old parts table of this tab whose every row has a part card (any option's), with
      // the heading right above it when that heading leads only to such tables; none when the element carries
      // data-cq-rel-keep-tables (round 4.2). The tab script later moves this content between the desktop panel and the
      // phone accordion as it is. Then marks the cards as the tab's last content when nothing a shopper sees follows
      // them (no bottom margin, cq-related-products.css).
      coverTables() {
        const box = this.parentElement;
        if (!box) return;
        // A card's SKU: data-cq-sku, else its "SKU ..." piece of the size and SKU line.
        const skus = new Set([...this.querySelectorAll('li[data-cq-part]')].map((li) => {
          const piece = [...(li.querySelector('.cq-rel-part-meta')?.children || [])].map(textOf).find((t) => /^SKU\s/.test(t));
          return (li.dataset.cqSku || (piece ? piece.slice(4) : '')).trim().toUpperCase();
        }).filter(Boolean));
        const cover = (node) => { node.hidden = true; node.setAttribute('data-cq-rel-covered', ''); };
        if (skus.size && !this.hasAttribute('data-cq-rel-keep-tables')) {
          const blocks = [];
          box.querySelectorAll('table').forEach((table) => {
            if (this.contains(table) || !tableCovered(table, skus)) return;
            // The table with any wrapper that holds nothing else (the editor's <div class="tg-wrap">).
            let block = table;
            while (block.parentElement && block.parentElement !== box && textOf(block.parentElement) === textOf(block)
              && block.parentElement.querySelectorAll(MEDIA).length === block.querySelectorAll(MEDIA).length + (block.matches(MEDIA) ? 1 : 0)) block = block.parentElement;
            blocks.push(block);
          });
          // Item 9: the pointing lines next to a left-out node (blank lines between are passed over). Walking down
          // from a left-out table, lines followed by a table that stays shown introduce that table and stay with it
          // (the part cards and their size comparison never count as that table).
          const leads = (node, step) => {
            const found = [];
            let n = node[step];
            for (; n && (isBlank(n) || isLead(n)); n = n[step]) if (isLead(n)) found.push(n);
            const introduces = step === 'nextSibling' && n && n.nodeType === Node.ELEMENT_NODE && n !== this && !n.contains(this)
              && [...(n.matches('table') ? [n] : n.querySelectorAll('table'))].some((table) => !table.closest('[hidden]'));
            if (!introduces) found.forEach(cover);
          };
          // Every table is left out first, so a line between two left-out tables sees both as left out.
          blocks.forEach(cover);
          blocks.forEach((block) => {
            // The theme's own table (snippets/cq-pdp-field-content.liquid) has its estimates note right under it.
            const note = block.nextElementSibling;
            if (note && note.matches('p.cq-field-note')) cover(note);
            leads(block, 'previousSibling');
            leads(block, 'nextSibling');
          });
          // Empty spacer paragraphs typed around a table that is left out (<p>&nbsp;</p>, <p><br></p>) go with it, so
          // they leave no gap where the table was.
          const spacers = (node, step) => {
            for (let n = node[step]; n && isBlank(n); n = n[step]) if (n.nodeType === Node.ELEMENT_NODE && n.tagName === 'P' && !n.hidden) cover(n);
          };
          // A heading goes too when everything between it and the next heading is now left out or blank.
          new Set(blocks.map((block) => {
            let node = block.previousSibling;
            while (node && !isHeading(node) && isBlank(node)) node = node.previousSibling;
            return node && isHeading(node) ? node : null;
          }).filter(Boolean)).forEach((heading) => {
            let node = heading.nextSibling;
            while (node && !isHeading(node)) {
              if (!isBlank(node)) return;
              node = node.nextSibling;
            }
            cover(heading);
            leads(heading, 'previousSibling');
          });
          box.querySelectorAll('[data-cq-rel-covered]').forEach((node) => {
            spacers(node, 'previousSibling');
            spacers(node, 'nextSibling');
          });
        }
        let next = this.nextSibling;
        while (next && isBlank(next)) next = next.nextSibling;
        this.toggleAttribute('data-cq-rel-last', !next);
      }

      // Item 10: the size comparison's "Selected" follows the option.
      markCompare(id) {
        this.querySelectorAll('[data-cq-rel-compare] tr[data-cq-cmp-variants]').forEach((tr) => {
          const on = tr.dataset.cqCmpVariants.split(' ').includes(String(id));
          tr.classList.toggle('is-selected', on);
          const tag = tr.querySelector('[data-cq-cmp-selected]');
          if (tag) tag.hidden = !on;
        });
      }

      showParts(id) {
        this.current = id;
        this.markCompare(id);
        const entry = this.map[id];
        const group = this.querySelector('[data-cq-rel-parts]');
        const list = this.querySelector('[data-cq-rel-parts-list]');
        if (!group || !list) return;
        list.querySelectorAll('[data-cq-part]').forEach((li) => { li.hidden = true; });
        entry.p.forEach(([partId, qty]) => {
          const li = list.querySelector(`[data-cq-part="${partId}"]`);
          if (!li) return;
          li.hidden = false;
          const q = li.querySelector('[data-cq-part-qty]');
          if (q) {
            q.hidden = qty < 2;
            q.querySelector('[data-cq-part-qty-n]').textContent = qty;
          }
          list.appendChild(li); // the option's own order
        });
        // AJ 2026-10-21: the option's Complete pack shows in its own panel after the individual parts.
        const packs = group.querySelector('[data-cq-rel-pack]');
        let packShown = false;
        packs?.querySelectorAll('[data-cq-pack-for]').forEach((li) => {
          const on = li.dataset.cqPackFor.includes(`,${id},`);
          li.hidden = !on;
          packShown = packShown || on;
        });
        if (packs) packs.hidden = !packShown;
        const shown = list.querySelector('[data-cq-part]:not([hidden])') !== null || packShown;
        if (shown) printHead(group);
        const forEl = this.querySelector('[data-cq-rel-for]');
        if (forEl) forEl.textContent = entry.t;
        group.hidden = !shown;
        list.scrollLeft = 0; // a phone swipe row starts again at the first part
        this.dispatchEvent(new CustomEvent('cq:rel-change', { bubbles: true }));
        // Focus never stays on a hidden part: it moves to the first part shown.
        if (group.contains(document.activeElement) && document.activeElement.closest('[hidden]')) list.querySelector('[data-cq-part]:not([hidden]) .cq-rel-part-name')?.focus();
      }
    });
  }

  if (!customElements.get('cq-related-products')) {
    customElements.define('cq-related-products', class extends HTMLElement {
      connectedCallback() {
        if (this.place()) return; // moved: the element connects again in its new place and starts there
        this.listeners?.abort();
        this.listeners = new AbortController();
        this.addEventListener('cq:rel-change', () => this.sync(), { signal: this.listeners.signal });
        if (!this.ready) {
          this.ready = true;
          // data-api-1 is read by name: dataset turns "-1" into the key "api-1", not "api1", so the fallback never ran
          // before (RELATED-3, 2026-10-14). Item 2: one request, complementary only.
          this.api = this.getAttribute('data-api-1');
          if (this.api) this.lazyLoad();
          this.watchAddons();
          this.pruneComp();
        } else {
          if (this.pending && !this.started) this.observe();
          if (this.bold) this.addonObserver?.observe(this.bold, { childList: true, subtree: true });
        }
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.listeners = null;
        this.observer?.disconnect();
        this.addonObserver?.disconnect();
      }

      // Item 7: the handles of the products this page sells as add-ons (the add-on app's values drawn so far).
      addons() {
        const set = new Set();
        this.bold?.querySelectorAll('[data-product-handle]').forEach((el) => {
          const handle = el.dataset.productHandle.trim().toLowerCase();
          if (handle) set.add(handle);
        });
        return set;
      }

      // A card's product handle: data-cq-handle (the section prints it), else its product link (/products/<handle>).
      static handleOf(li) {
        if (li.dataset.cqHandle) return li.dataset.cqHandle.toLowerCase();
        const href = li.querySelector('a.js-product-link[href], a[href*="/products/"]')?.getAttribute('href') || '';
        try {
          const m = /\/products\/([^/?#]+)/.exec(new URL(href, location.origin).pathname);
          return m ? decodeURIComponent(m[1]).toLowerCase() : '';
        } catch (e) { return ''; }
      }

      // Item 7: until the add-on app has drawn this product's add-ons (or answered with none), the "Goes well with"
      // group keeps its place but is not shown, so an add-on's card is taken out before anyone sees it. The app's
      // answer is its generate_option request for this product (as the option picker's first-visit wait reads it); the
      // app's script itself arrives after the page's load event (Shopify loads app scripts then), so its absence at
      // load proves nothing. If the app never answers, the group shows after ADDON_WAIT ms. Later drawings still take
      // cards out.
      watchAddons() {
        const groups = [...this.querySelectorAll('[data-cq-rel-comp], [data-cq-rel-cab]')]; // item 11: both groups wait
        const id = this.dataset.productId;
        const bold = groups.length && id && document.querySelector(`.bold_options[data-product-id="${CSS.escape(id)}"]`);
        if (!bold) return;
        this.bold = bold;
        let settle;
        const show = () => {
          clearTimeout(settle);
          clearTimeout(this.addonTimer);
          this.addonAnswer?.disconnect();
          this.addonAnswer = null;
          if (!this.addonWait) return;
          this.addonWait = false;
          this.pruneComp();
          groups.forEach((group) => group.style.removeProperty('visibility'));
        };
        this.addonObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.addonFrame);
          this.addonFrame = requestAnimationFrame(() => {
            this.pruneComp();
            // The app draws in steps: show once it has been still for a moment.
            if (this.addonWait && bold.querySelector('.bold_option')) { clearTimeout(settle); settle = setTimeout(show, 150); }
          });
        });
        this.addonObserver.observe(bold, { childList: true, subtree: true });
        if (bold.querySelector('.bold_option')) return; // already drawn: pruneComp runs next
        this.addonWait = true;
        groups.forEach((group) => { group.style.visibility = 'hidden'; });
        this.addonTimer = setTimeout(show, ADDON_WAIT);
        try {
          const answer = `/generate_option/${id}`;
          this.addonAnswer = new PerformanceObserver((list) => {
            if (!list.getEntries().some((entry) => entry.name.includes(answer))) return;
            this.addonAnswer?.disconnect();
            this.addonAnswer = null;
            // No add-ons drawn a moment after the answer: there are none (the app takes a few hundred ms to draw).
            setTimeout(() => { if (!bold.querySelector('.bold_option')) show(); }, ADDON_DRAW);
          });
          this.addonAnswer.observe({ type: 'resource', buffered: true });
        } catch (e) { /* no Resource Timing: the timer shows the group */ }
      }

      // Item 5: true when the row was moved under the buy box (or after the tabs) just now.
      place() {
        const where = SLOTS[this.dataset.place];
        if (!where) return false;
        const wrap = this.closest('[data-cq-rel-section]');
        const slot = document.querySelector(where);
        if (!wrap || !slot || wrap.parentElement === slot) return false;
        const id = wrap.dataset.cqRelSection;
        const old = moved.get(id);
        if (old && old !== wrap) old.remove();
        moved.set(id, wrap);
        slot.appendChild(wrap);
        return true;
      }

      lazyLoad() {
        this.pending = true;
        this.observe();
        const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.start(), { timeout: 4000 }) : setTimeout(() => this.start(), 1500));
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true });
      }

      observe() {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.start();
        }, { rootMargin: '0px 0px 1500px 0px' });
        this.observer.observe(this);
      }

      start() {
        if (this.started) return;
        this.started = true;
        this.observer?.disconnect();
        this.loadApi(this.api).then(() => { this.pending = false; this.pruneComp(); });
      }

      // A card with a product photo (card-product's main .cq-card-img image, not the hover one; its placeholder is an svg).
      static realCard(li) {
        return Boolean(li.querySelector('product-card .cq-card-media img.cq-card-img:not(.cq-card-img-alt)'));
      }

      // The server-rendered hand picks get the same card check, and every card of a product the page sells as an
      // add-on goes (item 7); the group hides, its heading back in its template, when none is left (with a fallback
      // request pending, that request decides).
      pruneComp() {
        const group = this.querySelector('[data-cq-rel-comp]');
        const list = this.querySelector('[data-cq-rel-comp-list]');
        const cab = this.querySelector('[data-cq-rel-cab]');
        const cabList = this.querySelector('[data-cq-rel-cab-list]');
        if ((!group || !list) && (!cab || !cabList)) return;
        const addons = this.addons();
        const prune = (ul) => [...ul.children]
          .filter((li) => !this.constructor.realCard(li) || addons.has(this.constructor.handleOf(li)))
          .forEach((li) => li.remove());
        if (group && list) {
          prune(list);
          if (!list.children.length && !this.pending && !group.hidden) {
            group.hidden = true;
            stashHead(group);
          }
        }
        // Item 11: Customers also bought hides below its minimum.
        if (cab && cabList) {
          prune(cabList);
          if (!cab.hidden && cabList.children.length < (Number(cab.dataset.cqRelMin) || 1)) {
            cab.hidden = true;
            stashHead(cab);
          }
        }
        this.sync();
      }

      sync() {
        const wrap = this.closest('.cq-rel-wrap') || this;
        wrap.hidden = !window.Shopify?.designMode && !this.pending && !this.querySelector('.cq-rel-group:not([hidden])');
      }

      loadApi(url) {
        const group = this.querySelector('[data-cq-rel-comp]');
        const list = this.querySelector('[data-cq-rel-comp-list]');
        if (!group || !list) return Promise.resolve(0);
        const exclude = this.dataset.exclude || '|';
        const max = Number(this.dataset.compMax) || 4;
        return fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const addons = this.addons();
            const items = [...doc.querySelectorAll('[data-cq-rel-api-result] > li[data-cq-pid]')]
              .filter((li) => !exclude.includes(`|${li.dataset.cqPid}|`) && this.constructor.realCard(li) && !addons.has(this.constructor.handleOf(li)))
              .slice(0, max);
            if (!items.length) return 0;
            list.replaceChildren(...items.map((li) => document.importNode(li, true)));
            printHead(group);
            group.hidden = false;
            return items.length;
          })
          .catch(() => 0);
      }
    });

    // Theme editor (item 5): the moved row lives outside its section.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:unload', (event) => {
        const id = event.detail && event.detail.sectionId;
        const wrap = id && moved.get(id);
        if (wrap && !event.target.contains(wrap)) { wrap.remove(); moved.delete(id); }
      });
      document.addEventListener('shopify:section:load', (event) => {
        if (!event.target.querySelector) return;
        moved.forEach((wrap) => {
          const place = wrap.querySelector('cq-related-products')?.dataset.place;
          const slot = SLOTS[place] && event.target.querySelector(SLOTS[place]);
          if (slot && !wrap.isConnected) slot.appendChild(wrap);
        });
      });
    }
  }
})();
